import {
  AddCircleOutline,
  CloseOutlined,
  DragIndicator,
  EditOutlined,
  InfoOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import LicenseContext from 'src/contexts/LicenseContext';
import {
  VEHICLE_ATTRIBUTE_CATEGORIES,
  VEHICLE_ATTRIBUTE_CATEGORY_LABELS,
  VehicleAttributeCategory,
  VehicleAttributeValueEntity,
} from 'src/entities/VehicleAttributeValue.entity';
import { useComposition } from 'src/hooks/useComposition';
import {
  useMutationVehicleAttributeValues,
  useVehicleAttributeValueUsageRequest,
  useVehicleAttributeValuesRequest,
} from 'src/hooks/useVehicleAttributeValues.request';

import { SectionCard } from './SectionCard';
import { SECTION_DEFS } from './sections';
import {
  aliasesForSave, apiErrorMessages, isConflictError, movedIdsWithinVisible, normalizeForMatch,
  validateDraft,
} from './vehicleAttributeEditor';

type ActiveFilter = 'all' | 'active' | 'inactive';

const ACTIVE_FILTERS: { key: ActiveFilter; label: string }[] = [
  { key: 'all', label: 'すべて' },
  { key: 'active', label: '有効' },
  { key: 'inactive', label: '無効' },
];

type EditorState = {
  open: boolean;
  /** 編集なら対象の値、追加なら null */
  target: VehicleAttributeValueEntity | null;
  name: string;
  aliases: string[];
  aliasInput: string;
  active: boolean;
  errors: string[];
};

/** 閉じたダイアログの状態。参照を使い回して毎回同じ初期値に戻す */
const closedEditor: EditorState = {
  open: false,
  target: null,
  name: '',
  aliases: [],
  aliasInput: '',
  active: true,
  errors: [],
};

const denseInputProps = { inputProps: { style: { fontSize: 13 } } };

/**
 * 車両表示設定（車両タイプ・車種・荷台高などの選択肢マスタ）。#329
 *
 * Why not 会社設定の保存ボタンに相乗り: 値は会社設定とは別の API（vehicle_attribute_values）で
 * 1 件ずつ保存する。react-hook-form の管理下に置くと画面下の保存ボタンが
 * 未保存扱いになり、保存済みの値をもう一度 PUT してしまう。
 */
export const VehicleAttributeSection: FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const licenseContext = useContext(LicenseContext);
  const { data, isError, refetch } = useVehicleAttributeValuesRequest();
  const {
    addVehicleAttributeValue,
    updateVehicleAttributeValue,
    reorderVehicleAttributeValues,
  } = useMutationVehicleAttributeValues();

  const [category, setCategory] = useState<VehicleAttributeCategory>(VEHICLE_ATTRIBUTE_CATEGORIES[0]);
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');
  const [editor, setEditor] = useState<EditorState>(closedEditor);
  const [saving, setSaving] = useState(false);
  /** 保存の待機中にダイアログが閉じられたかを、await のあとから読むための控え */
  const editorOpenRef = useRef(false);
  const [dragId, setDragId] = useState<number | null>(null);
  const [overId, setOverId] = useState<number | null>(null);

  const categoryLabel = VEHICLE_ATTRIBUTE_CATEGORY_LABELS[category];

  // 並べ替えの PATCH はそのカテゴリの id が全件必要なので、フィルタ前の全件を別に持つ
  const values = useMemo(
    () => [...(data?.[category] ?? [])].sort((a, b) => a.position - b.position),
    [data, category],
  );
  const rows = useMemo(() => {
    if (activeFilter === 'active') return values.filter((it) => it.active);
    if (activeFilter === 'inactive') return values.filter((it) => !it.active);
    return values;
  }, [values, activeFilter]);

  // 並べ替えは行をまたぐので API はカテゴリ単位で 409 を返す。1 行でも反映中なら止める
  const categoryIsUpdating = values.some((it) => it.updating);

  /**
   * 一覧が空のときに出す文言。
   *
   * 取得に失敗したときは何も出さない。失敗は下の Alert が伝えるので、ここで「読み込み中...」と
   * 出すと**永久に読み込み中に見える**（data は失敗しても undefined のままなので区別が要る）。
   */
  const emptyMessage = (() => {
    if (data) return '該当する値はありません。';
    return isError ? null : '読み込み中...';
  })();

  /**
   * 表示名を変えようとしているか。API の name_changed? と同じく完全一致で見るので、
   * 大小文字だけの違いも改名（＝既存データの書き換えが走る）として扱う。
   * Why not サンプルと同じ正規化比較: 「2t」→「2T」でも書き換えジョブは走るのに
   * 警告が出ず、2 分間その行が編集できなくなる理由が画面から分からない
   */
  const renamingTarget = editor.target;
  const isRenaming = Boolean(
    renamingTarget
    && editor.name.trim()
    && editor.name.trim() !== renamingTarget.name,
  );
  const { data: usage, isError: usageIsError } = useVehicleAttributeValueUsageRequest(
    renamingTarget?.id ?? null,
    isRenaming,
  );

  useEffect(() => {
    editorOpenRef.current = editor.open;
  }, [editor.open]);

  const notifyFailure = (error: unknown, fallback: string) => {
    const messages = apiErrorMessages(error);
    (messages.length > 0 ? messages : [fallback])
      .forEach((message) => enqueueSnackbar(message, { variant: 'error' }));
    // 409 は画面が反映中を知らないままボタンを出していたときだけ返るので、実態を取り直す
    if (isConflictError(error)) void refetch();
  };

  /**
   * 改名の影響を伝える文言。件数は最大 3〜4 秒かかるので待たせず、
   * 届いていない間は 0 件と書かずに確認中であることだけ出す（#313 の指摘）
   */
  const renameWarning = () => {
    const oldName = renamingTarget?.name ?? '';
    if (usage) {
      return `この値は現在、車両${usage.trucks.toLocaleString('ja-JP')}件・案件${usage.shipmentOrders.toLocaleString('ja-JP')}件・地点${usage.places.toLocaleString('ja-JP')}件で使用されています。名称変更後、順次新しい名称へ更新されます。旧表示名「${oldName}」は自動的にエイリアスへ追加されます。`;
    }

    const note = usageIsError ? '使用件数は取得できませんでした' : '使用件数を確認しています';
    return `名称変更後、この値を使っている既存データは順次新しい名称へ更新されます。旧表示名「${oldName}」は自動的にエイリアスへ追加されます。（${note}）`;
  };

  const openAdd = () => setEditor({ ...closedEditor, open: true });

  const openEdit = (row: VehicleAttributeValueEntity) => setEditor({
    open: true,
    target: row,
    name: row.name,
    aliases: [...row.aliases],
    aliasInput: '',
    active: row.active,
    errors: [],
  });

  const aliasComposition = useComposition();

  const addAlias = () => {
    const raw = editor.aliasInput.trim();
    if (!raw) return;

    setEditor((prev) => ({
      ...prev,
      // 突き合わせが大小文字を無視するので、表記だけ違う重複も足さず入力だけ捨てる
      aliases: prev.aliases.some((it) => normalizeForMatch(it) === normalizeForMatch(raw))
        ? prev.aliases
        : [...prev.aliases, raw],
      aliasInput: ''
    }));
  };

  const commit = async () => {
    // 非同期なので押しっぱなしにすると同じ内容が 2 回飛ぶ
    if (saving) return;

    const input = { name: editor.name, aliases: editor.aliases };
    const message = validateDraft(input, values, editor.target?.id ?? null, editor.target?.name);
    if (message) {
      setEditor((prev) => ({ ...prev, errors: [message] }));
      return;
    }

    // 改名しても旧表示名はサーバーがエイリアスへ足すので FE では足さない。
    // 逆に、元の名前へ戻すときは旧表示名がエイリアスに残っていて表示名と衝突するので、
    // 表示名になった値だけ外して送る（API の retain_renamed_alias と同じ規則）
    const draft = {
      name: editor.name.trim(),
      aliases: aliasesForSave(input, editor.target?.name),
      active: editor.active,
    };
    setSaving(true);
    try {
      if (editor.target) {
        const renamed = draft.name !== editor.target.name;
        await updateVehicleAttributeValue.mutateAsync({ id: editor.target.id, ...draft });
        /*
         * 改名すると既存データの書き換えジョブが積まれる。AsyncJob のポーリングは
         * session の has_pending_jobs か明示フラグで始まるが、session の再取得は 60 秒間隔なので、
         * それより早く終わるジョブでは一度も立たず、完了通知も reset_key による一覧の取り直しも
         * 届かないまま「変更反映中」が残る。
         *
         * Why not レスポンスの updating を見る: ジョブの投入に失敗したときも API は
         * 200・updating: false で返す（AsyncJob は dead）ので、最も重要な
         * 「旧名へ戻してやり直す」案内を取りに行けない。ジョブがレスポンス生成前に
         * 終わった場合も同じ。判断は「今回の保存が改名か」で行う
         */
        if (renamed) licenseContext.updateAsyncJobRequested(true);
      } else {
        await addVehicleAttributeValue.mutateAsync({ category, ...draft });
      }
      setEditor(closedEditor);
    } catch (error) {
      // FE の事前検証をすり抜けた重複（他の端末が同時に追加した等）と
      // 反映中の 409 は、API の文言をそのままダイアログに出す
      // 待っているあいだに閉じられていたら、閉じたダイアログへ書いても誰も読めない。
      // 通知で出して「保存できた」と思い込ませない
      if (!editorOpenRef.current) {
        notifyFailure(error, '保存に失敗しました。');
        return;
      }

      const messages = apiErrorMessages(error);
      setEditor((prev) => ({
        ...prev,
        errors: messages.length > 0 ? messages : ['保存に失敗しました。']
      }));
      if (isConflictError(error)) void refetch();
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row: VehicleAttributeValueEntity) => {
    try {
      // 画面は mutation 側の onMutate がキャッシュを先に書き換えるので、ここでは待たない。
      //
      // Why not 変えない項目も送り返す: API は送られてきたキーしか代入しないので、送らなければ変わらない。
      // 送ると、他の管理者が直した直後にこちらの古い値で巻き戻す（lost update）ぶんだけ悪くなる
      await updateVehicleAttributeValue.mutateAsync({
        id: row.id, name: row.name, aliases: row.aliases, active: !row.active,
      });
    } catch (error) {
      notifyFailure(error, '状態の変更に失敗しました。');
    }
  };

  const drop = async (targetId: number) => {
    const sourceId = dragId;
    setDragId(null);
    setOverId(null);
    if (sourceId === null) return;
    // draggable も切っているが、ここでも止める（API は 409 を返すので送っても無駄になる）
    if (categoryIsUpdating) return;

    const currentIds = values.map((it) => it.id);
    // 送るのは全件だが、入れ替えるのは見えている行だけ（隠れている行は動かさない）
    const ids = movedIdsWithinVisible(currentIds, rows.map((it) => it.id), sourceId, targetId);
    // 並びが変わらないときは受け取った配列がそのまま返る
    if (ids === currentIds) return;

    try {
      await reorderVehicleAttributeValues.mutateAsync({ category, ids });
    } catch (error) {
      notifyFailure(error, '表示順の変更に失敗しました。');
    }
  };

  return (
    <SectionCard
      id="vehicleAttr"
      title="車両表示設定"
      description="車両・案件・地点で選べる車両条件の選択肢を管理します。"
      icon={SECTION_DEFS.vehicleAttr.icon}
    >
      <Stack
        direction="row"
        spacing={0.75}
        data-testid="vehicle-attr-categories"
        sx={{ flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}
      >
        {VEHICLE_ATTRIBUTE_CATEGORIES.map((it) => (
          <Chip
            key={it}
            size="small"
            label={VEHICLE_ATTRIBUTE_CATEGORY_LABELS[it]}
            color={category === it ? 'primary' : 'default'}
            variant={category === it ? 'filled' : 'outlined'}
            onClick={() => setCategory(it)}
            sx={{ fontSize: 11.5, fontWeight: 700 }}
          />
        ))}
      </Stack>

      {/*
        追加ボタンはセクション見出しの右上ではなく、状態フィルタと同じ行でテーブルの真上に置く。
        サンプル（route-planner の VehicleAttributeSettings）がその配置で、
        「いま見ているカテゴリ・絞り込みに対して追加する」ことが操作の位置から読める
      */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        data-testid="vehicle-attr-filters"
        sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mr: 0.5 }}>状態</Typography>
          {ACTIVE_FILTERS.map(({ key, label }) => (
            <Chip
              key={key}
              size="small"
              label={label}
              color={activeFilter === key ? 'primary' : 'default'}
              variant={activeFilter === key ? 'filled' : 'outlined'}
              onClick={() => setActiveFilter(key)}
              sx={{ fontSize: 11, fontWeight: 600 }}
            />
          ))}
        </Stack>
        <Button
          size="small"
          startIcon={<AddCircleOutline />}
          sx={{ fontSize: 12, flexShrink: 0 }}
          // 読み込み前に追加すると、どのカテゴリに何件あるか分からないまま送ることになる
          disabled={!data}
          onClick={openAdd}
        >
          {`${categoryLabel}を追加`}
        </Button>
      </Stack>

      {/*
        取得に失敗したことを伝え、その場で取り直せるようにする。
        失敗しても data は undefined のままなので、伝えないと「読み込み中...」のまま止まり、
        追加ボタンも disabled={!data} で押せず、利用者にはやり直す手段が無くなる。

        Why not 一覧が空のときだけ出す: 取り直しに失敗したときは古い一覧が残っていることがあり、
        そのときこそ「いま見ているのは古い」と伝える必要がある。
        Why not 失敗時に追加ボタンを押せるようにする: どのカテゴリに何件あるか分からないまま
        送ることになるので、押せないままでよい。出口は取り直しのほうに置く。
      */}
      {isError && (
        <Alert
          severity="error"
          sx={{ mb: 1, fontSize: 12, py: 0, alignItems: 'center' }}
          action={(
            <Button size="small" sx={{ fontSize: 12 }} onClick={() => void refetch()}>
              再試行
            </Button>
          )}
        >
          選択肢を取得できませんでした。
        </Alert>
      )}

      <Box sx={{
        border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden',
      }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ width: 36 }} />
              <TableCell sx={{ fontSize: 11.5, fontWeight: 700 }}>表示名</TableCell>
              <TableCell sx={{ fontSize: 11.5, fontWeight: 700 }}>エイリアス</TableCell>
              <TableCell sx={{ fontSize: 11.5, fontWeight: 700, width: 92 }}>状態</TableCell>
              <TableCell sx={{ width: 140, whiteSpace: 'nowrap' }} />
            </TableRow>
          </TableHead>
          <TableBody data-testid="vehicle-attr-rows">
            {rows.map((row) => {
              /*
                掴んでいる行が「どこに入るか」を線で出す。背景色だけだと、どの行の上に居るかは
                分かっても上下どちらへ入るかが読めない。
                movedIds は対象の位置へ差し込むので、下へ動かすときは対象の下、
                上へ動かすときは対象の上に入る（意味論はそちらが正本）
              */
              const dragIndex = rows.findIndex((it) => it.id === dragId);
              const overIndex = rows.findIndex((it) => it.id === row.id);
              const isDropTarget = overId === row.id && dragIndex >= 0 && dragIndex !== overIndex;
              const insertsBelow = isDropTarget && dragIndex < overIndex;
              // TableRow の border はセル側の border に負けるので、行全体に inset の影で引く
              const dropLine = isDropTarget
                ? `inset 0 ${insertsBelow ? '-2px' : '2px'} 0 0 var(--vehicle-attr-drop-line)`
                : undefined;

              return (
              <TableRow
                key={row.id}
                hover
                draggable={!categoryIsUpdating}
                onDragStart={(event) => {
                  setDragId(row.id);
                  // Firefox は dataTransfer に何か入っていないとドラッグを始めない
                  event.dataTransfer.setData('text/plain', String(row.id));
                  event.dataTransfer.effectAllowed = 'move';
                }}
                onDragOver={(event) => {
                  // preventDefault しないとドロップ先として認められない
                  event.preventDefault();
                  if (dragId !== null && dragId !== row.id) setOverId(row.id);
                }}
                onDragLeave={() => setOverId((prev) => (prev === row.id ? null : prev))}
                onDrop={(event) => {
                  event.preventDefault();
                  void drop(row.id);
                }}
                onDragEnd={() => {
                  setDragId(null);
                  setOverId(null);
                }}
                sx={{
                  opacity: row.active ? (dragId === row.id ? 0.4 : 1) : 0.55,
                  bgcolor: isDropTarget ? 'action.selected' : undefined,
                  boxShadow: dropLine,
                  '--vehicle-attr-drop-line': (theme) => theme.palette.primary.main,
                }}
              >
                <TableCell sx={{
                  width: 36,
                  color: 'text.disabled',
                  cursor: categoryIsUpdating ? 'default' : 'grab',
                }}
                >
                  <Tooltip title={categoryIsUpdating
                    ? '変更反映中は表示順を変更できません'
                    : 'ドラッグして表示順を変更'}
                  >
                    <DragIndicator fontSize="small" />
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ fontSize: 12.5, fontWeight: 700 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <span>{row.name}</span>
                    {row.updating && (
                      <Chip
                        size="small"
                        label="変更反映中"
                        color="warning"
                        variant="outlined"
                        sx={{
                          fontSize: 10,
                          height: 18,
                          // LogTheme の warning は純黄色 (#FFD600) で白背景だと読めないので dark を使う
                          color: 'warning.dark',
                          borderColor: 'warning.dark',
                        }}
                      />
                    )}
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>
                  {row.aliases.length > 0 ? row.aliases.join(', ') : '—'}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.active ? '有効' : '無効'}
                    color={row.active ? 'success' : 'default'}
                    variant={row.active ? 'filled' : 'outlined'}
                    sx={{ fontSize: 10.5, height: 20 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title={row.updating ? '変更反映中は編集できません' : '編集'}>
                      {/* disabled なボタンは hover を受け取らないので span で包む */}
                      <span>
                        <IconButton
                          size="small"
                          aria-label="編集"
                          disabled={row.updating}
                          onClick={() => openEdit(row)}
                        >
                          <EditOutlined fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Button
                      size="small"
                      disabled={row.updating}
                      onClick={() => { void toggleActive(row); }}
                      sx={{ fontSize: 11, minWidth: 0, whiteSpace: 'nowrap' }}
                    >
                      {row.active ? '無効化' : '有効化'}
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {rows.length === 0 && emptyMessage && (
        <Typography sx={{
          fontSize: 12, color: 'text.secondary', py: 2, textAlign: 'center',
        }}
        >
          {/* 読み込み中に「ありません」と出すと設定が消えたように見えるので出し分ける */}
          {emptyMessage}
        </Typography>
      )}

      <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 1.5 }}>
        <InfoOutlined sx={{ fontSize: 15, color: 'text.secondary', mt: '1px' }} />
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
          有効な値だけが車両・案件・地点の選択肢に出ます。エイリアスは取込で表記の揺れ（「2トン」→「2t」）を
          表示名へ寄せるための別名です。ここに無い値は自動で作られないので、使う値は先に追加してください。
        </Typography>
      </Stack>

      <Dialog open={editor.open} onClose={() => setEditor(closedEditor)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>
          {editor.target ? `${categoryLabel}を編集` : `${categoryLabel}を追加`}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              size="small"
              label="表示名"
              required
              value={editor.name}
              onChange={(event) => setEditor((prev) => ({ ...prev, name: event.target.value }))}
              placeholder="例：13TW"
              InputLabelProps={{ sx: { fontSize: 12 } }}
              {...denseInputProps}
            />

            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75 }}>
                エイリアス（任意・複数）
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                {editor.aliases.map((alias) => (
                  <Chip
                    key={alias}
                    size="small"
                    label={alias}
                    onDelete={() => setEditor((prev) => ({
                      ...prev,
                      aliases: prev.aliases.filter((it) => it !== alias)
                    }))}
                    deleteIcon={<CloseOutlined aria-label={`エイリアス「${alias}」を外す`} />}
                    sx={{ fontSize: 11.5 }}
                  />
                ))}
              </Stack>
              <TextField
                size="small"
                fullWidth
                placeholder="入力してEnterで追加（例：13tウイング）"
                value={editor.aliasInput}
                onChange={(event) => setEditor((prev) => ({
                  ...prev, aliasInput: event.target.value,
                }))}
                onCompositionStart={aliasComposition.onCompositionStart}
                onCompositionEnd={aliasComposition.onCompositionEnd}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  // Enter でダイアログの既定ボタンが押されないようにする
                  event.preventDefault();
                  // 変換候補が出ている状態の Enter は「文字の確定」なので足さない。
                  // Why not preventDefault も飛ばす: 確定の Enter でも既定ボタンは押させない
                  if (aliasComposition.isComposing) return;
                  addAlias();
                }}
                {...denseInputProps}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>状態</Typography>
              <RadioGroup
                row
                value={editor.active ? 'active' : 'inactive'}
                onChange={(event) => setEditor((prev) => ({
                  ...prev, active: event.target.value === 'active',
                }))}
              >
                <FormControlLabel
                  value="active"
                  control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: 12.5 }}>有効</Typography>}
                />
                <FormControlLabel
                  value="inactive"
                  control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: 12.5 }}>無効</Typography>}
                />
              </RadioGroup>
            </Box>

            {isRenaming && (
              <Alert severity="warning" sx={{ fontSize: 12 }}>{renameWarning()}</Alert>
            )}

            {editor.errors.map((message) => (
              <Alert key={message} severity="error" sx={{ fontSize: 12 }}>{message}</Alert>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditor(closedEditor)} sx={{ fontSize: 12.5 }}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={() => { void commit(); }}
            sx={{ fontSize: 12.5 }}
          >
            {editor.target ? '変更する' : '追加する'}
          </Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
};

export default VehicleAttributeSection;
